/**
 * Settings Schemas
 * 
 * Zod validation schemas for settings forms
 */

import { z } from 'zod';

/**
 * Profile Settings Schema
 */
export const profileSettingsSchema = z.object({
  name: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
  email: z.string().email('Email inválido'),
  phone: z.string().optional(),
});

export type ProfileSettingsFormData = z.infer<typeof profileSettingsSchema>;

/**
 * Preferences Settings Schema
 * 
 * Note: Currency is configured per-company in Company Settings, not user preferences
 */
export const preferencesSettingsSchema = z.object({
  theme: z.enum(['light', 'dark', 'system']),
  dateFormat: z.enum(['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD']),
  numberFormat: z.enum(['1.234,56', '1,234.56']),
  language: z.enum(['pt-BR', 'en-US', 'es-ES']),
});

export type PreferencesSettingsFormData = z.infer<typeof preferencesSettingsSchema>;

/**
 * Notification Settings Schema
 */
export const notificationSettingsSchema = z.object({
  emailNotifications: z.boolean(),
  newTransactions: z.boolean(),
  budgetAlerts: z.boolean(),
  reportReady: z.boolean(),
  companyInvites: z.boolean(),
  systemUpdates: z.boolean(),
});

export type NotificationSettingsFormData = z.infer<typeof notificationSettingsSchema>;

/**
 * Password Change Schema
 */
export const passwordChangeSchema = z.object({
  currentPassword: z.string().min(6, 'Senha deve ter pelo menos 6 caracteres'),
  newPassword: z.string().min(6, 'Senha deve ter pelo menos 6 caracteres'),
  confirmPassword: z.string().min(6, 'Senha deve ter pelo menos 6 caracteres'),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: 'As senhas não coincidem',
  path: ['confirmPassword'],
});

export type PasswordChangeFormData = z.infer<typeof passwordChangeSchema>;

/**
 * Integrations Settings Schema
 * 
 * API keys and external service configurations.
 * Each user configures their own API keys.
 */
export const integrationsSettingsSchema = z.object({
  openai_api_key: z.string().optional(),
  exchangerate_api_key: z.string().optional(),
});

export type IntegrationsSettingsFormData = z.infer<typeof integrationsSettingsSchema>;
